import {
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import { FormikProps } from "formik";
import CustomDatePicker from "../../../../../../../../../components/CustomDatePicker";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Input from "../../../../../../../../../components/Input";
import Text from "../../../../../../../../../components/Text";
import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useReduceDisclosure } from "../../../../../../../../../hooks/use-reduce-disclosure";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import {
  AddCptCodes,
  renderSelectedCptCodes,
} from "../components/form-components/Add-CPT-Codes";
import {
  AddIcdCodes,
  renderSelectedIcdCodes,
} from "../components/form-components/Add-ICD-Codes";
import CustomSelect from "../../../../../../../../../components/CustomSelect";
import NumberInput from "../../../../../../../../../components/NumberInput";

// Clinical Assessment - Mental Status Examination options
const PRIMARY_MODALITY_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "disheveled", label: "Disheveled" },
  { value: "well-groomed", label: "Well-groomed" },
];
const BEHAVIOR_OPTIONS = [
  { value: "co operative", label: "Co operative" },
  { value: "guarded", label: "Guarded" },
  { value: "agitated", label: "Agitated" },
  { value: "withdrawn", label: "Withdrawn" },
];
const SPEECH_OPTIONS = [
  { value: "normal rate/tone", label: "Normal rate/tone" },
  { value: "pressured", label: "Pressured" },
  { value: "slow", label: "Slow" },
  { value: "loud", label: "Loud" },
];
const AFFECT_OPTIONS = [
  { value: "congruent", label: "Congruent" },
  { value: "incongruent", label: "Incongruent" },
  { value: "flat", label: "Flat" },
  { value: "labile", label: "Labile" },
];
const THOUGHT_PROCESS_OPTIONS = [
  { value: "logical/ linear", label: "Logical/ Linear" },
  { value: "tangential", label: "Tangential" },
  { value: "circumstantial", label: "Circumstantial" },
  { value: "disorganised", label: "Disorganised" },
];
const COGNITION_OPTIONS = [
  { value: "alert & oriented x4", label: "Alert & Oriented X4" },
  { value: "oriented x4", label: "Oriented X4" },
  { value: "confused", label: "Confused" },
];
const INSIGHT_OPTIONS = [
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
];
const INSIGHT_JUDGMENT_OPTIONS = [
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
];
const THOUGHT_CONTENT_CHECKBOX_ITEMS = [
  { value: "si", label: "SI (Suicidal Ideation)" },
  { value: "hi", label: "HI (Homicidal Ideation)" },
  { value: "delusions", label: "Delusions" },
  { value: "hallucinations", label: "Hallucinations" },
] as const;

interface PsychodynamicTherapyProps {
  formik: FormikProps<Form2Payload>;
}

const PsychodynamicTherapy = ({ formik }: PsychodynamicTherapyProps) => {
  const [icdDisclosure, cptDisclosure] = useReduceDisclosure(2);

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Session Information */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%">
          <CustomTitle>Session Information</CustomTitle>
        </Flex>
        <Grid item xs={12} md={6}>
          <CustomDatePicker
            label="Session Date"
            name="sessionDate"
            value={formik.values.sessionDate}
            onChange={(val) => formik.setFieldValue("sessionDate", val)}
            handleBlur={formik.handleBlur}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Input
            textFieldProps={{
              label: "Duration (Minutes)",
              name: "duration",
              placeholder: "Enter Duration",
              onBlur: formik.handleBlur,
              value: formik.values?.duration ?? "",
              onChange: formik.handleChange,
              type: "number",
              inputProps: { min: 0 },
            }}
          />
        </Grid>

        {/* Presenting Material & Session Focus */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Presenting Material & Session Focus</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Material Brought to Session"
            rows={3}
            name="materialBroughtToSession"
            placeholder="What the client brought up, discussed, or presented"
            onBlur={formik.handleBlur}
            value={formik.values?.materialBroughtToSession ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Session Focus/Themes"
            rows={3}
            name="sessionFocus"
            placeholder="Central themes explored during the session"
            onBlur={formik.handleBlur}
            value={formik.values?.sessionFocus ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Clinical Assessment - Mental Status Examination */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Clinical Assessment</CustomTitle>
          <Text
            variant="body2"
            sx={{ textTransform: "uppercase", fontWeight: 600 }}
          >
            Mental Status Examination
          </Text>
        </Flex>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Primary Modality</Text>
          <CustomSelect
            name="primaryModality"
            value={formik.values?.primaryModality ?? ""}
            options={PRIMARY_MODALITY_OPTIONS}
            placeholder="Select Primary Modality"
            onChange={(v) => formik.setFieldValue("primaryModality", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Behaviour</Text>
          <CustomSelect
            name="behavior"
            value={formik.values?.behavior ?? ""}
            options={BEHAVIOR_OPTIONS}
            placeholder="Select Observed Behaviour"
            onChange={(v) => formik.setFieldValue("behavior", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Speech</Text>
          <CustomSelect
            name="speech"
            value={formik.values?.speech ?? ""}
            options={SPEECH_OPTIONS}
            placeholder="Select Speech"
            onChange={(v) => formik.setFieldValue("speech", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Affect (Observed)</Text>
          <CustomSelect
            name="affect"
            value={formik.values?.affect ?? ""}
            options={AFFECT_OPTIONS}
            placeholder="Select Affect (Observed)"
            onChange={(v) => formik.setFieldValue("affect", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Thought Process</Text>
          <CustomSelect
            name="thoughtProcess"
            value={formik.values?.thoughtProcess ?? ""}
            options={THOUGHT_PROCESS_OPTIONS}
            placeholder="Select Thought Process"
            onChange={(v) => formik.setFieldValue("thoughtProcess", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Thought Content</Text>
          <Flex flexDirection="row" flexWrap="wrap" gap={1}>
            {THOUGHT_CONTENT_CHECKBOX_ITEMS.map((item) => {
              const thoughtContentStr = formik.values?.thoughtContent ?? "";
              const checkedKeys = thoughtContentStr
                ? thoughtContentStr
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [];
              const isChecked = checkedKeys.includes(item.value);
              return (
                <FormControlLabel
                  key={item.value}
                  control={
                    <Checkbox
                      name={`thoughtContent.${item.value}`}
                      checked={isChecked}
                      onChange={(e) => {
                        const newKeys = e.target.checked
                          ? [...checkedKeys, item.value]
                          : checkedKeys.filter((k) => k !== item.value);
                        formik.setFieldValue(
                          "thoughtContent",
                          newKeys.join(",")
                        );
                      }}
                    />
                  }
                  label={<Text>{item.label}</Text>}
                />
              );
            })}
          </Flex>
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Cognition</Text>
          <CustomSelect
            name="cognition"
            value={formik.values?.cognition ?? ""}
            options={COGNITION_OPTIONS}
            placeholder="Select Cognition"
            onChange={(v) => formik.setFieldValue("cognition", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Insight</Text>
          <CustomSelect
            name="insight"
            value={formik.values?.insight ?? ""}
            options={INSIGHT_OPTIONS}
            placeholder="Select Insight"
            onChange={(v) => formik.setFieldValue("insight", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Judgment</Text>
          <CustomSelect
            name="insightJudgment"
            value={formik.values?.insightJudgment ?? ""}
            options={INSIGHT_JUDGMENT_OPTIONS}
            placeholder="Select Judgment"
            onChange={(v) => formik.setFieldValue("insightJudgment", v)}
          />
        </Grid>

        {/* Free Association & Dream Work */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Free Association & Dream Work</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Free Associations"
            rows={3}
            name="freeAssociations"
            placeholder="Notable associations, thoughts, or spontaneous content"
            onBlur={formik.handleBlur}
            value={formik.values?.freeAssociations ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Dream Content"
            rows={3}
            name="dreamContent"
            placeholder="Dreams reported by the client"
            onBlur={formik.handleBlur}
            value={formik.values?.dreamContent ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Dream Analysis/Interpretation"
            rows={3}
            name="dreamAnalysis"
            placeholder="Symbolic meanings, latent content, and interpretations explored"
            onBlur={formik.handleBlur}
            value={formik.values?.dreamAnalysis ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Transference & Counter Transference */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Transference & Counter Transference</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Transference Observations"
            rows={3}
            name="transferenceObservations"
            placeholder="Client's transferential reactions, projections onto therapist"
            onBlur={formik.handleBlur}
            value={formik.values?.transferenceObservations ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Counter Transference Reflections"
            rows={3}
            name="counterTransferenceReflections"
            placeholder="Therapist's emotional responses and reflections"
            onBlur={formik.handleBlur}
            value={formik.values?.counterTransferenceReflections ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Resistance & Defense Mechanisms */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Resistance & Defense Mechanisms</CustomTitle>
          <Text variant="body2" sx={{ fontWeight: 600 }}>
            Defense Mechanisms Observed
          </Text>
        </Flex>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            {[
              [
                { value: "repression", label: "Repression" },
                { value: "displacement", label: "Displacement" },
                { value: "regression", label: "Regression" },
                { value: "splitting", label: "Splitting" },
              ],
              [
                { value: "denial", label: "Denial" },
                { value: "rationalization", label: "Rationalization" },
                { value: "sublimation", label: "Sublimation" },
                { value: "identification", label: "Identification" },
              ],
              [
                { value: "projection", label: "Projection" },
                { value: "reaction_formation", label: "Reaction formation" },
                { value: "intellectualization", label: "Intellectualization" },
                { value: "introjection", label: "Introjection" },
              ],
            ].map((column) => (
              <Grid item xs={12} md={4} key={column[0].value}>
                <Flex flexDirection="column" gap={0.5}>
                  {column.map((item) => {
                    const defenseStr =
                      formik.values?.defenseMechanismObserved ?? "";
                    const checkedKeys = defenseStr
                      ? defenseStr
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                      : [];
                    const isChecked = checkedKeys.includes(item.value);
                    return (
                      <FormControlLabel
                        key={item.value}
                        control={
                          <Checkbox
                            name={`defenseMechanismObserved.${item.value}`}
                            checked={isChecked}
                            onChange={(e) => {
                              const newKeys = e.target.checked
                                ? [...checkedKeys, item.value]
                                : checkedKeys.filter((k) => k !== item.value);
                              formik.setFieldValue(
                                "defenseMechanismObserved",
                                newKeys.join(",")
                              );
                            }}
                          />
                        }
                        label={<Text>{item.label}</Text>}
                      />
                    );
                  })}
                </Flex>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Unconscious Material & Patterns */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Unconscious Material & Patterns</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Unconscious Conflicts Explored"
            rows={3}
            name="unconsciousConflictsExplored"
            placeholder="Intrapsychic conflicts, wishes, fears identified"
            onBlur={formik.handleBlur}
            value={formik.values?.unconsciousConflictsExplored ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Early Childhood Experiences/Object Relations"
            rows={3}
            name="earlyChildhoodExperiences"
            placeholder="Connections to early experiences, attachment patterns, internalized objects"
            onBlur={formik.handleBlur}
            value={formik.values?.earlyChildhoodExperiences ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Relational Patterns"
            rows={3}
            name="relationalPatterns"
            placeholder="Repetitive interpersonal patterns, object relations dynamics"
            onBlur={formik.handleBlur}
            value={formik.values?.relationalPatterns ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Interpretations & Working Through */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Interpretations & Working Through</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Interpretations Offered"
            rows={3}
            name="interpretationsOffered"
            placeholder="Interpretations provided regarding unconscious material, defenses, transference"
            onBlur={formik.handleBlur}
            value={formik.values?.interpretationsOffered ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Client Response to Interpretations"
            rows={3}
            name="clientResponseToInterpretations"
            placeholder="How client received and responded to interpretations"
            onBlur={formik.handleBlur}
            value={formik.values?.clientResponseToInterpretations ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Working Through Process"
            rows={3}
            name="walkingThroughProcess"
            placeholder="Repeated exploration of conflicts and patterns"
            onBlur={formik.handleBlur}
            value={formik.values?.walkingThroughProcess ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Insights & Therapeutic Process */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Insights & Therapeutic Process</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Insights Developed"
            rows={3}
            name="insightsDeveloped"
            placeholder="New awareness or understanding achieved by client"
            onBlur={formik.handleBlur}
            value={formik.values?.insightsDeveloped ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Therapeutic Relationship/Alliance"
            rows={3}
            name="therapeuticRelationship"
            placeholder="Quality of therapeutic alliance, ruptures, repairs"
            onBlur={formik.handleBlur}
            value={formik.values?.therapeuticRelationship ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Progress Toward Treatment Goals"
            rows={3}
            name="progressTowardTreatmentGoal"
            placeholder="Assessment of progress in achieving therapeutic objectives"
            onBlur={formik.handleBlur}
            value={formik.values?.progressTowardTreatmentGoal ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Clinical Impression & Plan */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Clinical Impression & Plan</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Clinical Impression"
            rows={3}
            name="clinicalImpression"
            placeholder="Overall clinical assessment and formulation"
            onBlur={formik.handleBlur}
            value={formik.values?.clinicalImpression ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <StyleAddButton
            w="120px"
            onClick={() => icdDisclosure.open()}
            label="Add ICD Codes"
          />
          <AddIcdCodes formik={formik} disclosureHost={icdDisclosure} />
          {!!formik.values?.assessmentCodes?.length && (
            <Flex flexDirection={"column"} width={"100%"} gap={1}>
              {renderSelectedIcdCodes(
                formik.values?.assessmentCodes ?? [],
                (code) => {
                  formik.setFieldValue("assessmentCodes", [
                    ...(formik.values?.assessmentCodes ?? []).filter(
                      (it) => it.value !== code.value
                    ),
                  ]);
                },
                "20vh"
              )}
            </Flex>
          )}
        </Grid>

        {/* Plan for Next Session */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Plan for Next Session</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Plan for Next Session"
            rows={3}
            name="planForNextSession"
            placeholder="Areas to continue exploring in future sessions"
            onBlur={formik.handleBlur}
            value={formik.values?.planForNextSession ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Risk Assessment */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Risk Assessment</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <CustomTextArea
            label="Risk Assessment"
            rows={3}
            name="riskAssessment"
            placeholder="Safety concerns, suicidal/homicidal ideation assessment"
            onBlur={formik.handleBlur}
            value={formik.values?.riskAssessment ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Suicide Risk</Text>
          <RadioGroup
            row
            name="suicideRisk"
            value={formik.values?.suicideRisk ?? ""}
            onChange={(e) =>
              formik.setFieldValue("suicideRisk", e.target.value)
            }
          >
            {["None", "Low", "Moderate", "High"].map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Homicide Risk</Text>
          <RadioGroup
            row
            name="homicideRisk"
            value={formik.values?.homicideRisk ?? ""}
            onChange={(e) =>
              formik.setFieldValue("homicideRisk", e.target.value)
            }
          >
            {["None", "Low", "Moderate", "High"].map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </Grid>

        {/* Billing Information */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Billing Information</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <Flex
            flexDirection="row"
            alignItems="flex-end"
            gap={1}
            flexWrap="wrap"
            width="100%"
          >
            <Flex flex={1} minWidth={120} sx={{ width: "100%", minWidth: 0 }}>
              <NumberInput
                min={0}
                textFieldProps={{
                  fullWidth: true,
                  label: "Time Spend",
                  name: "timeSpend",
                  placeholder: "Enter Time",
                  onBlur: formik.handleBlur,
                  value: formik.values?.timeSpend ?? "",
                  onChange: formik.handleChange,
                }}
              />
            </Flex>
            <Flex flex={1} minWidth={120} sx={{ width: "100%", minWidth: 0 }}>
              <NumberInput
                min={0}
                textFieldProps={{
                  fullWidth: true,
                  label: "Documentation Time",
                  name: "documentationTime",
                  placeholder: "Enter time in minutes",
                  onBlur: formik.handleBlur,
                  value: formik.values?.documentationTime ?? "",
                  onChange: formik.handleChange,
                }}
              />
            </Flex>
            <Flex
              flex={1}
              minWidth={120}
              alignItems="center"
              justifyContent="flex-start"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name="billable"
                    checked={!!formik.values?.billable}
                    onChange={(e) =>
                      formik.setFieldValue("billable", e.target.checked)
                    }
                  />
                }
                label={<Text>Billable</Text>}
                sx={{ marginRight: 0, marginBottom: 0 }}
              />
            </Flex>
          </Flex>
        </Grid>
        <Grid item xs={12}>
          <StyleAddButton
            w="120px"
            onClick={() => cptDisclosure.open()}
            label="Add CPT Codes"
          />
          {!!formik.values?.procedureCodes?.length && (
            <Flex flexDirection={"column"} width={"100%"} gap={1}>
              {renderSelectedCptCodes(
                formik.values?.procedureCodes ?? [],
                (code) => {
                  formik.setFieldValue("procedureCodes", [
                    ...(formik.values?.procedureCodes ?? []).filter(
                      (it) => it.value !== code.value
                    ),
                  ]);
                },
                "20vh"
              )}
            </Flex>
          )}
          <AddCptCodes formik={formik} disclosureHost={cptDisclosure} />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="psychoeducationSessionRequired"
                checked={!!formik.values?.psychoeducationSessionRequired}
                onChange={(e) =>
                  formik.setFieldValue(
                    "psychoeducationSessionRequired",
                    e.target.checked
                  )
                }
              />
            }
            label={
              <Text variant="body1" fontWeight={500}>
                Psychoeducation Session Required?
              </Text>
            }
          />
        </Grid>
        {formik.values?.psychoeducationSessionRequired && (
          <Grid item xs={12}>
            <PsychoeducationTherapyForm formik={formik} />
          </Grid>
        )}
      </Grid>
    </Flex>
  );
};

export default PsychodynamicTherapy;
